// LMG Admin REST API Client
//
// Abstracts how LMG REST API calls should happen, and exposes repositories
// that allow a MUCH nicer API to interact with.
const Request = require('./request');

const {
  ApiError,
  NetworkError
} = require('./errors');

const PagedList = require('./entities/paged-list');

const AuthToken = require('./auth/auth-token');

const AuthProvider = require('./auth/auth-provider');

const {
  EmitterInterface,
  pipeEvent
} = require('./utils/events');

const events = require('./events');

const PaginationHelper = require('./utils/pagination-helper');

const logging = require('./logging');

const EnrichmentCache = require('./utils/enrichment-cache'); // Repositories


const AuthRepo = require('./repositories/auth-repo');

const AccessRepo = require('./repositories/access-repo');

const PolicyRepo = require('./repositories/policy-repo');

const RolesRepo = require('./repositories/roles-repo');

const PropertiesRepo = require('./repositories/properties-repo');

const BusinessesRepo = require('./repositories/businesses-repo');

const UploadsRepo = require('./repositories/uploads-repo');

const ImagesRepo = require('./repositories/images-repo');

const AddressesRepo = require('./repositories/addresses-repo');

const RegionsRepo = require('./repositories/regions-repo');

const UsersRepo = require('./repositories/users-repo');

const TagsRepo = require('./repositories/tags-repo');

const CategoriesRepo = require('./repositories/categories-repo');

const FlowsRepo = require('./repositories/flows-repo');

const SchedulesRepo = require('./repositories/schedules-repo');

const SocialRepo = require('./repositories/social/social-repo');

const {
  ADMIN_API_VERSION,
  ADMIN_SDK_NAME
} = require('./__versions__');

const SDK_VERSION = require('../package.json').version;

const MAX_RETRIES = 1;
const DEFAULT_RETRY_WAIT_TIME_S = 60;
/*
  Actual requests
 */

function performRequest(client
/*: ApiClient*/
, request
/*: Request*/
, authProvider
/*: AuthProvider*/
) {
  if (!request.method) throw new Error('Request.method cannot be empty');
  let startTime;
  const config
  /*: any*/
  = {
    method: request.method,
    headers: {
      'Content-Type': 'application/json',
      'X-Api-Version': ADMIN_API_VERSION,
      'X-Sdk-Name': ADMIN_SDK_NAME,
      'X-Sdk-Version': SDK_VERSION
    }
  };
  if (client.disableGzip) config.headers['accept-encoding'] = 'identity'; // TODO: Store err in request on incrementRetries and throw that error here

  if (request.retryAttempt > MAX_RETRIES) throw new Error('Max retries reached'); // include data

  if (request.method === 'GET') {// const data = request.serialize(request.data);
    // url = withQuery(url, data);
  } else if (request.method === 'POST' || request.method === 'PUT' || request.method === 'PATCH') {
    config.body = request.jsonBody();
  } else if (request.method === 'DELETE') {// There is no delete handling -- delete should never have a body
  } else {
    // TODO: Remove -- this should be enforcable via type system
    throw new Error(`Method ${request.method || 'UNKNOWN'} not supported`);
  } // Add auth headers


  client.debugLog('DEBUG: Auth allowed?', !!request.authType);
  return authProvider.getTokens().then((tokens
  /*: ?AuthToken*/
  ) => {
    if (request.authType === request.AUTH_TYPES.AUTH_REQUIRED && !(tokens && tokens.access)) {
      throw new Error('This request requires authentication');
    } // TODO: Auth required needs to actually reject here if there is no token


    if (!!request.authType && tokens != null && tokens.access != null) {
      const tokenPrefix = authProvider.getTokenPrefix();
      config.headers.Authorization = `${tokenPrefix} ${tokens.access}`;
      if (tokens.version != null) request.authTokenVersion = tokens.version;
    }
  }).then(() => {
    client.debugLog('DEBUG: Request', config.method, request.url(), '\n', config);
    startTime = Date.now();
    client.emit(events.REQUEST_STARTED, {
      method: config.method,
      path: request.path
    }); // `fetch()`` only throws on connectivity related errors (as per
    // https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch)
    //
    // TODO: consider this case & write handlers with custom error
    // .catch(err => console.log(err)) // eslint-disable-line

    return fetch(request.url(), config).catch(err => {
      throw new NetworkError(err);
    });
  }).then((res
  /*: Response*/
  ) => {
    // Some responses have no body
    // These will throw so we explicitly catch res.json() if it fails and coerce it to an empty result
    if (!res.ok) {
      return res.json().catch(() => ({})).then(json => {
        throw new ApiError(res, json);
      });
    }

    return res.json().catch(() => ({}));
  }).catch(err => {
    client.debugLog('DEBUG: Error', err.message, err.context, '\n', err);
    client.debugLog('DEBUG: Context', JSON.stringify(err.context, null, 2), '\n');
    client.emit(events.REQUEST_FAILED, {
      method: config.method,
      url: request.path,
      duration: startTime ? Date.now() - startTime : 0
    });

    if (err.isNetworkError && request.method === 'GET') {
      request.incrementRetries();
      return performRequest(client, request, authProvider);
    }

    if (err.status === 429) {
      if (client.scripting) {
        // wait for rate limits
        let waitTime;

        try {
          waitTime = parseInt(err.response.headers.get('X-Rate-Limit-Reset'), 10) || DEFAULT_RETRY_WAIT_TIME_S;
        } catch (innerErr) {
          client.debugLog('DEBUG: Error', innerErr.message, '\n', innerErr);
          waitTime = DEFAULT_RETRY_WAIT_TIME_S;
        }

        client.debugLog(`DEBUG: 429 - waiting for ${waitTime}s`);
        request.incrementRetries();
        return new Promise(res => setTimeout(() => res(performRequest(client, request, authProvider)), waitTime * 1000));
      }
    }

    if (err.status === 401) {
      // Handle expired tokens
      //
      // This error comes from cognito, hence it doesn't have a messageId
      // that can easily be used to ensure its the correct message
      // Refresh the token, then retry the request before considering
      // it an actual failure
      client.debugLog('DEBUG: Refresh Token');
      request.incrementRetries();
      return authProvider.refreshToken().then(() => performRequest(client, request, authProvider));
    }

    throw err;
  }).then(json => {
    client.debugLog('DEBUG: Response', '\n', JSON.stringify(json, 0, 2));
    client.emit(events.REQUEST_SUCCEEDED, {
      method: config.method,
      url: request.path,
      duration: startTime ? Date.now() - startTime : 0
    });
    return json;
  });
}

function makePageReq(page
/*: Object*/
, origReq
/*: Request*/
)
/*: Request*/
{
  const req = origReq.clone();

  if (req.useCursor === true || req.useCursor !== false && page.cursor && !page.offset) {
    req.data.cursor = page.cursor;
    req.data.offset = undefined; // clone copies all data
  } else {
    req.data.offset = page.offset;
    req.data.cursor = undefined; // clone copies all data
  }

  req.data.limit = page.limit;
  return req;
}

function makeScrollReq(scroll
/*: any*/
, origReq
/*: Request*/
)
/*: Request*/
{
  const req = origReq.clone();
  req.data.cursor = scroll.cursor;
  return req;
}

const identity = x => x;

class Client extends EmitterInterface {
  // top-level repos
  constructor(options
  /*: ApiClientOptions*/
  ) {
    super();

    if (options.baseUrl != null) {
      // baseUrl can override anything
      this.baseUrl = options.baseUrl;
    } else {
      // hardcoded environment-based urls
      switch (options.env) {
        case 'local':
          this.baseUrl = 'https://admin-api.lmg-local.ca';
          break;

        case 'dev':
          this.baseUrl = 'https://admin-api.lmg-dev.ca';
          break;

        case 'stage':
          this.baseUrl = 'https://admin-api.lmg-stage.ca';
          break;

        case 'prod':
        default:
          this.baseUrl = 'https://admin-api.loopmediagroup.com';
          break;
      }
    }

    this.Request = Request;

    this.debugLog = options.debugLog || (() => undefined);

    this.authProvider = options.authProvider || new AuthProvider();
    this.scripting = options.scripting || false;
    this.disableGzip = options.disableGzip || false; // Pipe auth events

    pipeEvent(events.AUTH_CHANGED, this.authProvider, this);
    pipeEvent(events.AUTH_LOGOUT, this.authProvider, this);
    pipeEvent(events.AUTH_EXPIRED, this.authProvider, this); // ---
    // Helpers
    // ---

    this.enrichment = new EnrichmentCache();
    this.pagination = new PaginationHelper(this);
    this.log = logging; // ---
    // Repositories
    // ---

    this.auth = new AuthRepo(this, this.authProvider);
    this.access = new AccessRepo(this);
    this.policy = new PolicyRepo(this);
    this.roles = new RolesRepo(this);
    this.properties = new PropertiesRepo(this);
    this.businesses = new BusinessesRepo(this);
    this.uploads = new UploadsRepo(this);
    this.images = new ImagesRepo(this);
    this.addresses = new AddressesRepo(this);
    this.regions = new RegionsRepo(this);
    this.users = new UsersRepo(this);
    this.tags = new TagsRepo(this);
    this.categories = new CategoriesRepo(this);
    this.flows = new FlowsRepo(this);
    this.schedules = new SchedulesRepo(this);
    this.social = new SocialRepo(this);
  } // ---
  // HTTP Verbs
  // ---


  get(req
  /*: Request*/
  )
  /*: Promise<any>*/
  {
    return this.execute('GET', req, this.authProvider).then(res => {
      // parse & return paginated results automatically
      //
      // pagination reponse format:
      //   Legacy Syntax
      //      { payload: [], page: { prev: null, next: null } }
      //   New Syntax
      //      { payload: [], page: { previous: null, next: null } }
      //
      if (Array.isArray(res.payload)) {
        const pages
        /*: { next?: Request, prev?: Request, current?: number, max?: number }*/
        = {};
        const page = res.page || {};
        pages.helpers = this.pagination;

        if (req.useScroll && page.scroll) {
          pages.next = makeScrollReq(page.scroll, req);
        } else {
          // add the current and max page data
          if (page.index) {
            pages.current = page.index.current;
            pages.max = page.index.max;
          }

          if (page.next) {
            pages.next = makePageReq(page.next, req);
          } // TODO: Remove support for page.prev when the entire API is migrated over


          if (page.prev || page.previous) {
            pages.prev = makePageReq(page.prev || page.previous, req);
          }
        }

        return new PagedList(res.payload.map(i => req.parse(i)), pages);
      }

      const payload = res.payload;
      if (payload) return req.parse(res.payload); // EDGECASE:
      // ONE request (/meta) does NOT have a .payload param...

      return req.parse(res);
    }).then(req.transform || identity);
  }

  put(req
  /*: Request*/
  )
  /*: Promise<any>*/
  {
    return this.execute('PUT', req, this.authProvider).then(res => {
      const payload = res.payload;
      if (payload) return req.parse(res.payload);
      return req.parse(res);
    });
  }

  patch(req
  /*: Request*/
  )
  /*: Promise<any>*/
  {
    return this.execute('PATCH', req, this.authProvider).then(res => req.parse(res.payload));
  }

  post(req
  /*: Request*/
  )
  /*: Promise<any>*/
  {
    return this.execute('POST', req, this.authProvider).then(res => req.parse(res.payload));
  }

  delete(req
  /*: Request*/
  )
  /*: Promise<boolean>*/
  {
    return this.execute('DELETE', req, this.authProvider).then(() => true);
  }
  /**
   * Internal request executor method
   *
   * DO NOT call outside of Client / AuthRetryStrategy
   */


  execute(method
  /*: HttpMethod*/
  , req
  /*: Request*/
  , authProvider
  /*: AuthProvider*/
  ) {
    if (!this.baseUrl) throw new Error(`Cannot make request from client without baseUrl`);
    req.baseUri = this.baseUrl;
    req.method = method;
    return performRequest(this, req, authProvider);
  }

}

module.exports = Client;